import React from "react";
import DOMPurify from "dompurify";
import { formatCalculationDetails } from "./formatCalculationDetails";
import "./result-display.css";
export default function ResultDisplay({
  result = {},
  isLoading,
  currentLoadingScore = 5,
  showFinalScore,
  showCalculationDetails,
  setShowCalculationDetails,
  showReferences,
  setShowReferences,
  showResponsibleApplication,
  setShowResponsibleApplication,
  shouldBlinkResponsibleApplication,
  setShouldBlinkResponsibleApplication,
  showOurBigTechs,
  setShowOurBigTechs,
  t,
  showThumbs = true,
  onThumbsUp,
  onThumbsDown,
  thumbsUpAnimated,
  thumbsDownAnimated,
  thumbsUpMessage,
  thumbsDownMessage,
  banditManchotImageSrc = "/images/bandit-manchot.png",
  closeButton,
  junkFoodPrizeVoteSection,
  onToggleSection,
  children
}) {
  const {
    score,
    explanation,
    references,
    position,
    company,
    calculationDetails,
    notAProductJobOffer
  } = result ?? {};
  const displayScore = notAProductJobOffer ? 1 : score;
  const visibleScore = isLoading ? currentLoadingScore : displayScore;
  const imageScore = Math.min(5, Math.max(1, Math.round(displayScore ?? 1)));
  const levels = [{
    color: "#058040",
    letter: "A"
  }, {
    color: "#84bc2d",
    letter: "B"
  }, {
    color: "#fdc705",
    letter: "C"
  }, {
    color: "#ef7c00",
    letter: "D"
  }, {
    color: "#e03d0b",
    letter: "E"
  }];
  const scoreToIndex = {
    5: 0,
    4: 1,
    3: 2,
    2: 3,
    1: 4
  };
  const activeIndex = scoreToIndex[imageScore] ?? 0;
  const currentLevel = levels[activeIndex] || levels[0];
  const baseAngles = [-126, -54, 18, 90, 162];
  const rotation = 0;
  const openOnly = section => {
    setShowCalculationDetails?.(section === 'calculation_details');
    setShowReferences?.(section === 'references');
    setShowResponsibleApplication?.(section === 'responsible_application');
    setShowOurBigTechs?.(section === 'our_big_techs');
  };
  const toggleCalc = () => {
    const next = !showCalculationDetails;
    if (next) openOnly('calculation_details');else setShowCalculationDetails?.(false);
    onToggleSection?.('calculation_details', next);
  };
  const toggleRefs = () => {
    const next = !showReferences;
    if (next) openOnly('references');else setShowReferences?.(false);
    onToggleSection?.('references', next);
  };
  const toggleResp = () => {
    const next = !showResponsibleApplication;
    if (next) {
      openOnly('responsible_application');
      setShouldBlinkResponsibleApplication?.(false);
    } else setShowResponsibleApplication?.(false);
    onToggleSection?.('responsible_application', next);
  };
  const toggleBigTechs = () => {
    const next = !showOurBigTechs;
    if (next) openOnly('our_big_techs');else setShowOurBigTechs?.(false);
    onToggleSection?.('our_big_techs', next);
  };
  const safeReferences = references ? DOMPurify.sanitize(references, {
    ADD_ATTR: ["target"]
  }) : "";
  return /*#__PURE__*/React.createElement("div", {
    className: "result-container"
  }, closeButton && /*#__PURE__*/React.createElement("div", {
    className: "result-container-close"
  }, closeButton), position && /*#__PURE__*/React.createElement("h2", {
    className: "position-title"
  }, t("PositionTitle"), " ", position), company && /*#__PURE__*/React.createElement("h3", {
    className: "company-name"
  }, t("CompanyLabel"), " ", company), /*#__PURE__*/React.createElement("div", {
    className: `nutri-score-loading${!isLoading && showFinalScore ? " final-score" : ""}`
  }, /*#__PURE__*/React.createElement("svg", {
    width: "80",
    height: "80",
    viewBox: "0 0 120 120",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "54",
    fill: "none",
    stroke: "#038141",
    strokeWidth: "10",
    strokeDasharray: "67.9 271.4",
    transform: `rotate(${baseAngles[0] + rotation} 60 60)`
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "54",
    fill: "none",
    stroke: "#85bb2f",
    strokeWidth: "10",
    strokeDasharray: "67.9 271.4",
    transform: `rotate(${baseAngles[1] + rotation} 60 60)`
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "54",
    fill: "none",
    stroke: "#fecb01",
    strokeWidth: "10",
    strokeDasharray: "67.9 271.4",
    transform: `rotate(${baseAngles[2] + rotation} 60 60)`
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "54",
    fill: "none",
    stroke: "#ee8005",
    strokeWidth: "10",
    strokeDasharray: "67.9 271.4",
    transform: `rotate(${baseAngles[3] + rotation} 60 60)`
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "54",
    fill: "none",
    stroke: "#e63e0f",
    strokeWidth: "10",
    strokeDasharray: "67.9 271.4",
    transform: `rotate(${baseAngles[4] + rotation} 60 60)`
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "49",
    fill: "#ffffff"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "60",
    cy: "60",
    r: "48",
    fill: currentLevel.color
  }), /*#__PURE__*/React.createElement("text", {
    x: "60",
    y: "64",
    textAnchor: "middle",
    dominantBaseline: "middle",
    fill: "#ffffff",
    fontSize: "40",
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: "700"
  }, currentLevel.letter))), !isLoading && score != null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
    className: "explanation-text"
  }, explanation), /*#__PURE__*/React.createElement("div", {
    className: "toggle-sections-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "toggle-desktop"
  }, /*#__PURE__*/React.createElement("div", {
    className: "toggle-buttons-row"
  }, calculationDetails && /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showCalculationDetails ? "active" : ""}`,
    onClick: toggleCalc
  }, showCalculationDetails ? "▼" : "▶", " ", t("CalcDetailsTitle")), /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showReferences ? "active" : ""}`,
    onClick: toggleRefs
  }, showReferences ? "▼" : "▶", " ", t("ReferencesTitle")), /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showResponsibleApplication ? "active" : ""} ${shouldBlinkResponsibleApplication ? "blink" : ""}`,
    onClick: toggleResp
  }, showResponsibleApplication ? "▼" : "▶", " ", t("ResponsibleApplicationTitle")), /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showOurBigTechs ? "active" : ""}`,
    onClick: toggleBigTechs
  }, showOurBigTechs ? "▼" : "▶", " ", t("OurBigTechsTitle"))), /*#__PURE__*/React.createElement("div", {
    className: "shared-content-area"
  }, showCalculationDetails && calculationDetails && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "calculation-details-text",
    dangerouslySetInnerHTML: {
      __html: formatCalculationDetails(calculationDetails, t)
    }
  })), showReferences && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "references-text",
    dangerouslySetInnerHTML: {
      __html: safeReferences
    }
  })), showResponsibleApplication && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("img", {
    src: banditManchotImageSrc,
    alt: "Bandit Manchot",
    className: "bandit-manchot-image"
  }), /*#__PURE__*/React.createElement("div", {
    className: "responsible-application-text"
  }, /*#__PURE__*/React.createElement("p", null, displayScore <= 2 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationDirectLossCandidate")), " ", t("ResponsibleApplicationDirectLossDetails"), " ", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationDirectLossState")), " ", t("ResponsibleApplicationDirectLossStateDetails")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationGeneralLossCandidate")), " ", t("ResponsibleApplicationGeneralLossDetails"), " ", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationGeneralLossState")), " ", t("ResponsibleApplicationGeneralLossStateDetails"))), /*#__PURE__*/React.createElement("p", {
    className: "calculation-link"
  }, /*#__PURE__*/React.createElement("a", {
    href: "https://www.linkedin.com/pulse/do-startups-destroy-more-value-than-theycreate-jacques-giraudel-hop4e/",
    target: "_blank",
    rel: "noopener noreferrer",
    className: "calculation-link-text"
  }, t("CalculationLinkText"))))), showOurBigTechs && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "our-big-techs-text"
  }, /*#__PURE__*/React.createElement("p", null, t("OurBigTechsContent")), /*#__PURE__*/React.createElement("p", {
    className: "calculation-link"
  }, /*#__PURE__*/React.createElement("a", {
    href: "https://www.linkedin.com/pulse/our-big-techs-regulation-innovation-professions-europe-giraudel-r7bve/",
    target: "_blank",
    rel: "noopener noreferrer",
    className: "calculation-link-text"
  }, t("OurBigTechsLinkText"))))))), /*#__PURE__*/React.createElement("div", {
    className: "toggle-mobile"
  }, calculationDetails && /*#__PURE__*/React.createElement("div", {
    className: "toggle-option-block"
  }, /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showCalculationDetails ? "active" : ""}`,
    onClick: toggleCalc
  }, showCalculationDetails ? "▼" : "▶", " ", t("CalcDetailsTitle")), showCalculationDetails && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "calculation-details-text",
    dangerouslySetInnerHTML: {
      __html: formatCalculationDetails(calculationDetails, t)
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "toggle-option-block"
  }, /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showReferences ? "active" : ""}`,
    onClick: toggleRefs
  }, showReferences ? "▼" : "▶", " ", t("ReferencesTitle")), showReferences && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "references-text",
    dangerouslySetInnerHTML: {
      __html: safeReferences
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "toggle-option-block"
  }, /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showResponsibleApplication ? "active" : ""} ${shouldBlinkResponsibleApplication ? "blink" : ""}`,
    onClick: toggleResp
  }, showResponsibleApplication ? "▼" : "▶", " ", t("ResponsibleApplicationTitle")), showResponsibleApplication && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("img", {
    src: banditManchotImageSrc,
    alt: "Bandit Manchot",
    className: "bandit-manchot-image"
  }), /*#__PURE__*/React.createElement("div", {
    className: "responsible-application-text"
  }, /*#__PURE__*/React.createElement("p", null, displayScore <= 2 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationDirectLossCandidate")), " ", t("ResponsibleApplicationDirectLossDetails"), " ", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationDirectLossState")), " ", t("ResponsibleApplicationDirectLossStateDetails")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationGeneralLossCandidate")), " ", t("ResponsibleApplicationGeneralLossDetails"), " ", /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("strong", null, t("ResponsibleApplicationGeneralLossState")), " ", t("ResponsibleApplicationGeneralLossStateDetails"))), /*#__PURE__*/React.createElement("p", {
    className: "calculation-link"
  }, /*#__PURE__*/React.createElement("a", {
    href: "https://www.linkedin.com/pulse/do-startups-destroy-more-value-than-theycreate-jacques-giraudel-hop4e/",
    target: "_blank",
    rel: "noopener noreferrer",
    className: "calculation-link-text"
  }, t("CalculationLinkText")))))), /*#__PURE__*/React.createElement("div", {
    className: "toggle-option-block"
  }, /*#__PURE__*/React.createElement("button", {
    className: `toggle-button ${showOurBigTechs ? "active" : ""}`,
    onClick: toggleBigTechs
  }, showOurBigTechs ? "▼" : "▶", " ", t("OurBigTechsTitle")), showOurBigTechs && /*#__PURE__*/React.createElement("div", {
    className: "toggle-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "our-big-techs-text"
  }, /*#__PURE__*/React.createElement("p", null, t("OurBigTechsContent")), /*#__PURE__*/React.createElement("p", {
    className: "calculation-link"
  }, /*#__PURE__*/React.createElement("a", {
    href: "https://www.linkedin.com/pulse/our-big-techs-regulation-innovation-professions-europe-giraudel-r7bve/",
    target: "_blank",
    rel: "noopener noreferrer",
    className: "calculation-link-text"
  }, t("OurBigTechsLinkText"))))))), junkFoodPrizeVoteSection, /*#__PURE__*/React.createElement("div", {
    className: "aimention-row"
  }, /*#__PURE__*/React.createElement("p", {
    className: "aimention-text"
  }, t("aiMention")), showThumbs && /*#__PURE__*/React.createElement("div", {
    className: "thumbs-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "thumbs-button-wrapper"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onThumbsUp,
    className: `thumbs-button ${thumbsUpAnimated ? "thumbs-animated" : ""}`
  }, "\uD83D\uDC4D"), thumbsUpMessage && /*#__PURE__*/React.createElement("p", {
    className: "thumbs-message"
  }, /*#__PURE__*/React.createElement("em", null, thumbsUpMessage))), /*#__PURE__*/React.createElement("div", {
    className: "thumbs-button-wrapper"
  }, /*#__PURE__*/React.createElement("button", {
    onClick: onThumbsDown,
    className: `thumbs-button ${thumbsDownAnimated ? "thumbs-animated" : ""}`
  }, "\uD83D\uDC4E"), thumbsDownMessage && /*#__PURE__*/React.createElement("p", {
    className: "thumbs-message"
  }, /*#__PURE__*/React.createElement("em", null, thumbsDownMessage))))))), children);
}